import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Api from "../../utils/api";
import { setRoute } from "../../store/actions/routeActions";
import { useDispatch } from "react-redux";
import Button from "../../components/Button";
import ModalTyC from "../../components/solicitud/ModalTyC";
import Modal from "../../components/Modal";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SkeletonTable } from "../../components/Skeleton";

const date = new Date();

enum PlanState {
  ON_LOAD,
  LOAD,
  NONE,
}

export default function ConfeccionarPlan() {
  const [onLoad, setOnLoad] = useState(true);
  const [showTyc, setShowTyc] = useState(false);
  const [planSelected, setPlanSelected] = useState("");
  const [primerTal, setPrimerTal] = useState("");
  const [initialDate, setInitialDate] = useState("");
  const [tyc, setTyc] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [planes, setPlanes] = useState<
    Array<{ id: string; plan: string; needAmount: boolean }>
  >([]);

  const navigate = useNavigate();

  const [planGnerado, setPlanGenerado] = useState<{
    totalDebt: number;
    moneda: string;
    plans: Array<{
      date1: string;
      date2: string;
      amount: string;
      senia: string;
      edit: boolean;
      id: string;
    }>;
    load: PlanState;
    message: string;
  }>({
    moneda: "$",
    totalDebt: 0,
    plans: [],
    load: PlanState.NONE,
    message: "",
  });
  const [generarPlan, setGenerarPlan] = useState<{
    message: string;
    show: boolean;
    date: string;
  }>({
    message: "",
    show: false,
    date: "",
  });

  const { id = "-1" } = useParams();

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      setRoute({
        section: "pasajero",
        sub: "cuotas-y-pago",
        cp: id,
      })
    );
  }, [dispatch, id]);

  useEffect(() => {
    if (!planSelected || !initialDate) return;
    const fecha = initialDate.match(/(\w+)[-](\w+)[-](\w+)/);

    if (!fecha) return;

    // verificamos si el plan seleccionado necesida pimer talon
    let pi_primer_tal: undefined | string = undefined;
    if (planes.find((plan) => plan.id === planSelected)?.needAmount) {
      pi_primer_tal = primerTal;
    }

    setErrorMessage("");

    setPlanGenerado({
      totalDebt: 0,
      load: PlanState.ON_LOAD,
      plans: [],
      message: "",
      moneda: "$",
    });

    Api("consumir")
      .post({
        servicio: "paxGenerarPlan",
        param: {
          pco_plan: planSelected,
          pf_inicio: `${fecha[3]}/${fecha[2]}/${fecha[1]}`,
          pi_primer_tal,
          pco_pasajero: id,
        },
      })
      .then((res) => {
        if (res.mensaje.tipo !== "M") {
          setErrorMessage(res.mensaje.descripcion);
          setPlanGenerado({
            totalDebt: 0,
            load: PlanState.NONE,
            plans: [],
            message: res.MENSAJE,
            moneda: "$",
          });
          return;
        }
        setPlanGenerado({
          message: "",
          totalDebt: +res.PDEUDA_TOTAL,
          moneda: res.PMONEDA,
          load: PlanState.LOAD,
          plans: res.PCUR_PLAN.map((plan: any) => ({
            date1: plan.FECHA1,
            date2: plan.FECHA2,
            amount: plan.IMPORTE,
            senia: plan.IMP_SENIA ?? "",
            edit: plan.MODIFICAR === "S",
            id: plan.NU_TALON,
          })),
        });
      });
  }, [planSelected, id, initialDate, primerTal, planes]);

  useEffect(() => {
    setOnLoad(true);
    Api("consumir")
      .post({
        servicio: "paxCuotas",
        param: {
          pco_pasajero: id,
        },
      })
      .then((res) => {
        const prefGenPlan = res.PREF_GENPLAN?.[0];
        setTyc(res.PREF_TYC?.[0]?.TYC || "");
        if (prefGenPlan) {
          const fechahasta = res.PF_PC_HASTA.match(
            /^(\d+)\/(\d+)\/(\d+)$/
          ) as string[];
          setGenerarPlan({
            message: prefGenPlan.MENSAJE,
            show: prefGenPlan.M_GENERAR_PLAN === "S",
            date: `${fechahasta[3]}-${fechahasta[2].padStart(2, "0")}-${
              fechahasta[1]
            }`,
          });

          setPlanes(
            res.PREF_PLANES.map((plan: any) => ({
              needAmount: plan.PEDIR_IMPORTE === "S",
              plan: plan.PLAN,
              id: plan.CO_PLAN,
            }))
          );
        }
      })
      .finally(() => {
        setOnLoad(false);
      });
  }, [id]);

  const handleCargarPlan = () => {
    // verificamos si el plan seleccionado necesida pimer talon
    let pi_primer_tal: undefined | string = undefined;
    if (planes.find((plan) => plan.id === planSelected)?.needAmount) {
      pi_primer_tal = primerTal;
    }

    const fecha = initialDate.match(/(\w+)[-](\w+)[-](\w+)/);

    if (!fecha) return;

    setShowTyc(false);
    setOnLoad(true);

    Api("consumir")
      .post({
        servicio: "paxCargarPlan",
        param: {
          pco_pasajero: id,
          pf_inicio: `${fecha[3]}/${fecha[2]}/${fecha[1]}`,
          pi_primer_tal,
          pco_plan: planSelected,
        },
      })
      .then((res) => {
        if (res.PM_ERROR === "M") {
          setShowSuccess(true);
          return;
        }
      })
      .finally(() => {
        setOnLoad(false);
      });
  };

  return (
    <div className="container-data">
      <h3 className="color-platform mb-5">Confeccionar mi plan de pago</h3>
      <div>
        <form
          style={{
            maxWidth: "500px",
            margin: "0 auto",
          }}
        >
          <div className="form-group mb-3">
            <label className="form-label">
              Vencimiento de la primera cuota del plan
            </label>
            <input
              onChange={(e) => setInitialDate(e.target.value)}
              type="date"
              min={`${date.getFullYear()}-${(date.getMonth() + 1)
                .toString()
                .padStart(2, "0")}-${date
                .getDate()
                .toString()
                .padStart(2, "0")}`}
              max={generarPlan.date}
              className="form-control"
            />
          </div>
          <div className="form-group mb-3">
            <label className="form-label">Plan</label>
            <select
              className="form-control"
              name=""
              id=""
              onChange={(e) => setPlanSelected(e.target.value)}
            >
              <option value="">Selecciona un plan</option>
              {planes.map((plan, index) => (
                <option key={index} value={plan.id}>
                  {plan.plan}
                </option>
              ))}
            </select>
          </div>
          {planes.find((plan) => plan.id === planSelected)?.needAmount && (
            <div className="form-group mb-3">
              <label className="form-label">Valor Pre-Viaje</label>
              <input
                onChange={(e) => setPrimerTal(e.target.value)}
                type="number"
                className="form-control"
              />
            </div>
          )}
        </form>
        <p className="text-danger text-center">{errorMessage}</p>
      </div>
      {planGnerado.message && (
        <div className="alert alert-danger text-center mt-4">
          {planGnerado.message}
        </div>
      )}
      {planGnerado.load === PlanState.ON_LOAD && <SkeletonTable />}
      {planGnerado.load === PlanState.LOAD && (
        <div>
          <p className="alert alert-info text-center mt-4">
            Valor total: {planGnerado.moneda}
            {planGnerado.totalDebt.toFixed(2)}
          </p>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th className="th-platform">Talon</th>
                  <th className="th-platform">1° Vencimiento</th>
                  <th className="th-platform">2° Vencimiento</th>
                  <th className="th-platform">Importe</th>
                  <th className="th-platform">Seña</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {planGnerado.plans.map((plan, index) => (
                  <tr key={index}>
                    <td>{plan.id}</td>
                    <td>{plan.date1}</td>
                    <td>{plan.date2}</td>
                    <td>
                      {planGnerado.moneda}
                      {plan.amount}
                    </td>
                    <td>{plan.senia}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-center mb-4">
            <Button
              title="Confirmar"
              onPress={() => setShowTyc(true)}
              onLoad={onLoad}
            />
          </div>
        </div>
      )}
      <ModalTyC
        show={showTyc}
        body={tyc}
        onClose={() => setShowTyc(false)}
        onAcept={handleCargarPlan}
      />
      <Modal
        title="Plan generado correctamente"
        btnAceptar="Aceptar"
        show={showSuccess}
        onClose={() => {}}
        hideClose={true}
        onAceptar={() => navigate("/")}
      >
        <div style={{ textAlign: "center" }}>
          <FontAwesomeIcon
            color="#20c997"
            icon={faCheckCircle}
            fontSize={100}
          />
          <p className="mt-3">Click en aceptar para ir al inicio</p>
        </div>
      </Modal>
    </div>
  );
}
